<template>
  <div class="component-testeditorstage">
    <v-card flat>
      <v-card-text>
        <v-stepper vertical class="elevation-0"
          v-if="status && status.currentStep"
          v-model="currentStep">

          <!-- v-alert type="info" class="mb-2">
            <strong>Debug Stage:</strong> Domande trovate: {{ questions.length }}
          </v-alert -->

          <div v-for="(question, index) of questions" :key="`question-${index}`">
            <v-stepper-step
              :step="index+1"
              :complete="mode === 'edit' ? true : isQuestionComplete(question)"
              :editable="mode === 'edit'"
              edit-icon="mdi-pencil"
              :color="questionColor(question)"
              :class="{
                'question-selectable': mode === 'edit',
                'question-selected': mode === 'edit' && selectedQuestionIndex === index
              }"
              @click="onQuestionClick(index)">
              <div class="question-header">
                <div class="question-title-wrapper">
                  <span class="question-title"
                    :class="{ 'question-hidden': mode === 'edit' && question.visibility === 'always_hidden' }"
                    v-html="getQuestionTitle(question)">
                  </span>
                  <v-tooltip
                    v-if="mode === 'edit' && hasConditions(question)"
                    bottom
                    :open-delay="200"
                    :close-delay="0">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        :color="getConditionColor(question)"
                        icon
                        v-bind="attrs"
                        v-on="on"
                        class="condition-warning-btn ml-2 mr-2">
                        <v-icon>{{ getConditionIcon(question) }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ getConditionTooltip(question) }}</span>
                  </v-tooltip>
                </div>
                <v-btn
                  v-if="mode === 'edit'"
                  color="error"
                  small
                  icon
                  outlined
                  @click.stop="removeQuestion(index)"
                  class="delete-btn">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </v-stepper-step>

            <v-stepper-content :step="index+1">
              <v-card class="mb-4" elevation="0">
                <v-card-text>
                  <div class="question-answers">
                    <template v-if="question.type === 'buttons'">
                      <draggable
                        :list="question.answers"
                        handle=".sortHandle"
                        draggable=".sortableAnswer"
                        :disabled="mode !== 'edit'"
                        @end="onAnswersReorder(index)"
                        tag="div"
                        class="draggable-buttons-container"
                      >
                        <v-col
                          v-for="(answer, answerIndex) in visibleAnswers(question)"
                          :key="`answer-${answer.id || answerIndex}`"
                          :cols="getCols(question)"
                          class="sortableAnswer"
                          :class="{
                            'answer-selectable': mode === 'edit',
                            'answer-selected': mode === 'edit' && selectedQuestionIndex === index && selectedAnswerIndex === answerIndex,
                          }"
                          @click="onAnswerClick(index, answerIndex, $event)"
                        >
                          <div class="answer-wrapper">
                            <v-btn
                              block
                              x-large
                              class="bigbutton"
                              :class="{ 'answer-hidden': mode === 'edit' && answer.visibility === 'always_hidden' }"
                              @click="mode === 'preview' ? handleAnswerSelection(index, answerIndex) : null"
                            >
                              {{ answer.title }}
                            </v-btn>

                            <!-- Icona drag in alto a destra -->
                            <div class="answer-actions-drag">
                              <v-btn
                                v-if="mode === 'edit'"
                                style="cursor: move"
                                icon
                                class="sortHandle"
                                :style="{ padding: '0 8px' }"
                              >
                                <v-icon>mdi-drag</v-icon>
                              </v-btn>
                            </div>

                            <!-- Icona delete in basso a destra -->
                            <div class="answer-actions-delete">
                              <v-btn
                                v-if="mode === 'edit'"
                                color="error"
                                icon
                                small
                                outlined
                                @click.stop="removeAnswer(index, answerIndex)"
                                :style="{ padding: '0 8px' }"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </div>

                            <div class="answer-conditions">
                              <v-tooltip
                                v-if="mode === 'edit' && hasConditions(answer)"
                                bottom
                                :open-delay="200"
                                :close-delay="0"
                              >
                                <template #activator="{ on, attrs }">
                                  <v-btn
                                    :color="getConditionColor(answer)"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    class="condition-warning-btn"
                                    :style="{ padding: '0 8px' }"
                                  >
                                    <v-icon>{{ getConditionIcon(answer) }}</v-icon>
                                  </v-btn>
                                </template>
                                <span>{{ getConditionTooltip(answer) }}</span>
                              </v-tooltip>
                            </div>
                          </div>
                        </v-col>

                        <!-- Pulsante Nuova Risposta per buttons -->
                        <v-col
                          v-if="mode === 'edit'"
                          :cols="getCols(question)"
                          class="add-answer-col"
                          slot="footer"
                        >
                          <div class="answer-wrapper">
                            <v-btn
                              block
                              x-large
                              class="bigbutton add-answer-btn"
                              @click="addAnswer(index)"
                            >
                              <v-icon left>mdi-plus</v-icon>
                              {{ $t('test.editor.stage.newAnswer') }}
                            </v-btn>
                          </div>
                        </v-col>
                      </draggable>
                    </template>

                    <template v-else-if="question.type === 'radio'">
                      <v-row>
                        <v-col>
                          <draggable
                            :list="question.answers"
                            handle=".sortHandle"
                            draggable=".sortableAnswer"
                            :disabled="mode !== 'edit'"
                            @end="onAnswersReorder(index)"
                            class="d-flex flex-column">
                            <div v-for="(answer, answerIndex) in visibleAnswers(question)"
                              :key="`answer-${answerIndex}`"
                              class="answer-item sortableAnswer"
                              :class="{
                                'answer-selectable': mode === 'edit',
                                'answer-selected': mode === 'edit' && selectedQuestionIndex === index && selectedAnswerIndex === answerIndex
                              }"
                              @click="onAnswerClick(index, answerIndex, $event)">
                              <div class="d-flex align-center">
                                <v-radio
                                  v-model="selectedAnswer"
                                  :label="answer.title"
                                  color="success"
                                  :value="answer.title"
                                  :class="{
                                    'answer-hidden': mode === 'edit' &&
                                      answer.visibility === 'always_hidden'
                                  }"
                                  class="flex-grow-1"
                                  @change="mode === 'preview' ?
                                    handleAnswerSelection(index, answerIndex) : null">
                                </v-radio>
                                <div v-if="mode === 'edit'" class="answer-actions-horizontal">
                                  <v-btn
                                    style="cursor: move"
                                    icon
                                    class="sortHandle"
                                    :style="{ padding: '0 8px' }">
                                    <v-icon>mdi-drag</v-icon>
                                  </v-btn>
                                  <v-btn
                                    color="error"
                                    small
                                    icon
                                    outlined
                                    @click.stop="removeAnswer(index, answerIndex)">
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                  <v-tooltip
                                    v-if="hasConditions(answer)"
                                    bottom
                                    :open-delay="200"
                                    :close-delay="0">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-btn
                                        :color="getConditionColor(answer)"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        class="condition-warning-btn"
                                        :style="{ padding: '0 8px' }">
                                        <v-icon>{{ getConditionIcon(answer) }}</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>{{ getConditionTooltip(answer) }}</span>
                                  </v-tooltip>
                                </div>
                              </div>
                            </div>
                          </draggable>

                          <!-- Pulsante Nuova Risposta per radio -->
                          <div v-if="mode === 'edit'" class="answer-item add-answer-item">
                            <div class="d-flex align-center">
                              <v-radio
                                disabled
                                :label="$t('test.editor.stage.newAnswer')"
                                color="success"
                                class="flex-grow-1 add-answer-radio">
                              </v-radio>
                              <div class="answer-actions-horizontal">
                                <v-btn
                                  color="success"
                                  small
                                  icon
                                  outlined
                                  @click="addAnswer(index)">
                                  <v-icon small>mdi-plus</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </template>

                    <template v-else-if="question.type === 'checkboxes'">
                      <v-row>
                        <v-col cols="12">
                          <draggable
                            :list="question.answers"
                            handle=".sortHandle"
                            draggable=".sortableAnswer"
                            :disabled="mode !== 'edit'"
                            @end="onAnswersReorder(index)"
                            class="d-flex flex-column">
                            <div v-for="(answer, answerIndex) in visibleAnswers(question)"
                              :key="`answer-${answerIndex}`"
                              class="answer-item sortableAnswer"
                              :class="{
                                'answer-selectable': mode === 'edit',
                                'answer-selected': mode === 'edit' &&
                                  selectedQuestionIndex === index && selectedAnswerIndex === answerIndex
                              }"
                              @click="onAnswerClick(index, answerIndex, $event)">
                              <div class="d-flex align-center">
                                <v-checkbox
                                  v-if="mode === 'preview'"
                                  v-model="selectedCheckboxes"
                                  :label="answer.title"
                                  color="success"
                                  :value="answer"
                                  :class="{
                                    'answer-hidden': mode === 'edit' &&
                                      answer.visibility === 'always_hidden'
                                  }"
                                  class="flex-grow-1">
                                </v-checkbox>
                                <v-checkbox
                                  v-else
                                  v-model="selectedAnswer"
                                  :label="answer.title"
                                  color="success"
                                  :value="answer.title"
                                  :class="{
                                    'answer-hidden': mode === 'edit' &&
                                      answer.visibility === 'always_hidden'
                                  }"
                                  class="flex-grow-1">
                                </v-checkbox>
                                <div v-if="mode === 'edit'" class="answer-actions-horizontal">
                                  <v-btn
                                    style="cursor: move"
                                    icon
                                    class="sortHandle"
                                    :style="{ padding: '0 8px' }">
                                    <v-icon>mdi-drag</v-icon>
                                  </v-btn>
                                  <v-btn
                                    color="error"
                                    small
                                    icon
                                    outlined
                                    @click.stop="removeAnswer(index, answerIndex)">
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                  <v-tooltip
                                    v-if="hasConditions(answer)"
                                    bottom
                                    :open-delay="200"
                                    :close-delay="0">
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-btn
                                        :color="getConditionColor(answer)"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        class="condition-warning-btn"
                                        :style="{ padding: '0 8px' }">
                                        <v-icon>{{ getConditionIcon(answer) }}</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>{{ getConditionTooltip(answer) }}</span>
                                  </v-tooltip>
                                </div>
                              </div>
                            </div>
                          </draggable>

                          <!-- Pulsante Conferma scelte per checkboxes in modalità preview -->
                          <div v-if="mode === 'preview'" class="answer-item confirm-checkboxes-item">
                            <v-btn
                              color="success"
                              text
                              :disabled="selectedCheckboxes.length === 0"
                              @click="confirmCheckboxSelections(index)"
                              class="confirm-checkboxes-btn">
                              {{ $t('test.editor.stage.confirmChoices') }}
                            </v-btn>
                          </div>

                          <!-- Pulsante Nuova Risposta per checkboxes -->
                          <div v-if="mode === 'edit'" class="answer-item add-answer-item">
                            <div class="d-flex align-center">
                              <v-checkbox
                                disabled
                                :label="$t('test.editor.stage.newAnswer')"
                                color="success"
                                class="flex-grow-1 add-answer-checkbox">
                              </v-checkbox>
                              <div class="answer-actions-horizontal">
                                <v-btn
                                  color="success"
                                  small
                                  icon
                                  outlined
                                  @click="addAnswer(index)">
                                  <v-icon small>mdi-plus</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </template>
                  </div>
                </v-card-text>
              </v-card>
            </v-stepper-content>
          </div>

          <!-- Pulsante Nuova Domanda contestuale -->
          <div v-if="mode === 'edit' && stage.name !== 'stage4'">
            <v-stepper-step
              :step="questions.length + 1"
              :complete="true"
              :editable="true"
              edit-icon="mdi-plus"
              color="success"
              class="add-question-step"
              @click="addQuestion">
              <div class="question-header">
                <div class="question-title-wrapper">
                  <span class="question-title add-question-title">
                    {{ $t('test.editor.stage.addNewQuestion') }}
                  </span>
                </div>
              </div>
            </v-stepper-step>
          </div>
        </v-stepper>

        <v-alert
          v-if="questions.length === 0"
          type="info"
          text
          class="mt-4">
          <v-icon left>mdi-information</v-icon>
          {{ $t('test.editor.stage.noQuestions') }}
        </v-alert>
      </v-card-text>
    </v-card>

    <v-card-actions v-if="mode === 'preview'">
      <v-btn text color="orange" @click="onRestart()">
        {{ $t('test.editor.stage.restart') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn depressed color="success" @click="onContinue()" :disabled="!canContinue">
        {{ $t('test.editor.stage.continue') }}
      </v-btn>
    </v-card-actions>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { evaluateConditions as evaluateConditionsUtil, isItemVisible } from '@/utils/wizard-conditions';

export default {
  name: 'TestEditorStage',

  components: {
    draggable,
  },

  props: ['value', 'stage', 'test', 'mode', 'selectedQuestionIndex', 'selectedAnswerIndex'],

  data: () => ({
    status: null,
    selectedAnswer: null,
    selectedCheckboxes: [], // Per gestire le selezioni multiple delle checkbox
  }),

  created() {
    this.status = this.value;
  },

  watch: {
    value: {
      handler(newValue) {
        this.status = newValue;
      },
      deep: true,
    },

    status(newValue) {
      this.$emit('input', newValue);
    },

    // Rimosso: avanzamento automatico causa problemi con il stepper
    // isStageComplete(newValue) {
    //   if (newValue && this.mode === 'preview') {
    //     this.$nextTick(() => {
    //       this.onContinue();
    //     });
    //   }
    // },
  },

  computed: {
    stageNamesUpToCurrent() {
      const allStages = ['stage1', 'stage2', 'stage3', 'stage4'];
      const idx = allStages.indexOf(this.stage.name);
      return allStages.slice(0, idx + 1);
    },

    questions() {
      if (!this.stage || !Array.isArray(this.stage.questions)) return [];

      return this.stage.questions.filter((question) => this.isQuestionVisible(question));
    },

    currentStep: {
      get() {
        return this.status ? this.status.currentStep : 1;
      },
      set(val) {
        if (this.status) {
          this.status.currentStep = val;
        }
      },
    },

    isStageComplete() {
      // Verifica se tutte le domande visibili sono completate
      return this.questions.every((question) => this.isQuestionComplete(question));
    },

    canContinue() {
      // Il pulsante continua è abilitato solo se tutte le domande visibili sono complete.
      return this.isStageComplete;
    },
  },

  methods: {
    /**
     * Verifica se una domanda è visibile basandosi sulle condizioni configurate.
     * Il nuovo sistema usa:
     * - visibility: 'always_visible', 'always_hidden', 'conditional'
     * - conditions: array di { questionId, answerId }
     *
     * Logica:
     * - Se visibility === 'always_visible': mostra TRANNE se condizioni soddisfatte
     * - Se visibility === 'always_hidden': nascondi TRANNE se condizioni soddisfatte
     */
    isQuestionVisible(question) {
      if (this.mode === 'edit') return true;
      return isItemVisible(question, this.status.stages, this.stageNamesUpToCurrent);
    },

    isAnswerVisible(answer) {
      if (this.mode === 'edit') return true;
      return isItemVisible(answer, this.status.stages, this.stageNamesUpToCurrent);
    },

    evaluateConditions(conditions) {
      return evaluateConditionsUtil(conditions, this.status.stages, this.stageNamesUpToCurrent);
    },

    findQuestionById(stageName, questionId) {
      const stage = this.getStageByName(stageName);
      if (!stage || !stage.questions) return null;

      return stage.questions.find((q) => q.id === questionId);
    },

    getStageByName(stageName) {
      // Trova lo stage nel test corrente
      const stageIndex = ['stage1', 'stage2', 'stage3', 'stage4'].indexOf(stageName);
      if (stageIndex === -1 || !this.test || !this.test[stageIndex]) return null;

      return this.test[stageIndex];
    },

    visibleAnswers(question) {
      if (!question.answers) return [];

      return question.answers.filter((answer) => this.isAnswerVisible(answer));
    },

    isQuestionComplete(question) {
      // In modalità edit, nessuna domanda deve apparire come completata
      if (this.mode === 'edit') {
        return false;
      }

      if (!this.status || !this.status.stages[this.stage.name] || !question.id) {
        return false;
      }
      const stageQuestions = this.status.stages[this.stage.name].questions;
      const questionResponse = stageQuestions[question.id];
      return questionResponse && questionResponse.complete;
    },

    questionColor(question) {
      // In modalità edit, tutti gli step devono avere il colore primario
      if (this.mode === 'edit') {
        return 'primary';
      }
      return this.isQuestionComplete(question) ? 'success' : 'primary';
    },

    getCols(question) {
      // Griglia sempre a 3 colonne (4 cols per cella)
      return 4;
    },

    hasConditions(item) {
      return item && item.conditions && item.conditions.length > 0;
    },

    getConditionIcon(item) {
      if (!this.hasConditions(item)) return null;

      if (item.visibility === 'always_visible') {
        return 'mdi-eye-off';
      }
      if (item.visibility === 'always_hidden') {
        return 'mdi-eye';
      }
      return 'mdi-cog';
    },

    getConditionColor(item) {
      if (!this.hasConditions(item)) return 'warning';

      if (item.visibility === 'always_visible') {
        return 'error';
      }
      if (item.visibility === 'always_hidden') {
        return 'success';
      }
      return 'warning';
    },

    getConditionTooltip(item) {
      if (!this.hasConditions(item)) return '';

      const conditionCount = item.conditions.length;

      if (item.visibility === 'always_visible') {
        return conditionCount === 1
          ? this.$t('test.editor.config.conditions.tooltipHiddenSingle')
          : this.$t('test.editor.config.conditions.tooltipHiddenMultiple', { count: conditionCount });
      }
      if (item.visibility === 'always_hidden') {
        return conditionCount === 1
          ? this.$t('test.editor.config.conditions.tooltipShownSingle')
          : this.$t('test.editor.config.conditions.tooltipShownMultiple', { count: conditionCount });
      }
      return this.$t('test.editor.config.conditions.tooltipConditionsCount', { count: conditionCount });
    },

    onQuestionClick(questionIndex) {
      if (this.mode === 'edit') {
        const stageIndex = this.getStageIndex();
        this.$emit('question-selected', stageIndex, questionIndex);
        // In modalità edit, non modificare status.currentStep perché
        // currentStep è gestito dalla computed
      } else {
        // In modalità preview, naviga alla domanda
        this.status.currentStep = questionIndex + 1;
        this.$emit('input', this.status);
      }
    },

    onAnswerClick(questionIndex, answerIndex, event) {
      if (this.mode === 'edit') {
        event.preventDefault();
        event.stopPropagation();
        const stageIndex = this.getStageIndex();
        this.$emit('answer-selected', stageIndex, questionIndex, answerIndex);
      } else {
        // In modalità preview, gestisci la selezione della risposta
        // MA NON per le checkbox che hanno il loro proprio sistema di conferma
        const question = this.questions[questionIndex];
        if (question && question.type !== 'checkboxes') {
          this.handleAnswerSelection(questionIndex, answerIndex);
        }
      }
    },

    handleAnswerSelection(questionIndex, answerIndex) {
      const question = this.questions[questionIndex];
      if (!question || !question.id) return;

      // Ottieni le risposte visibili per questa domanda
      const visibleAnswers = this.visibleAnswers(question);
      const answer = visibleAnswers[answerIndex];

      if (!answer) return;

      // Salva la risposta nello stato usando l'ID della domanda
      if (!this.status.stages[this.stage.name].questions) {
        this.$set(this.status.stages[this.stage.name], 'questions', {});
      }

      this.$set(this.status.stages[this.stage.name].questions, question.id, {
        complete: true,
        answer: answer.title,
        answerId: answer.id,
      });

      // Avanza alla prossima domanda
      this.$nextTick(() => {
        const nextQuestionIndex = this.questions.findIndex(
          (q, idx) => idx > questionIndex
            && this.isQuestionVisible(q) && !this.isQuestionComplete(q),
        );
        if (nextQuestionIndex !== -1) {
          this.status.currentStep = nextQuestionIndex + 1;
        } else if (this.isStageComplete) {
          // Se non ci sono altre domande visibili, controlla se lo stage è completo
          this.status.currentStep = this.questions.length + 1; // Collassa l'ultima domanda
        }
      });

      // Emetti l'aggiornamento dello stato
      this.$emit('input', this.status);
    },

    getStageIndex() {
      // Trova l'indice dello stage corrente
      const stageNames = ['stage1', 'stage2', 'stage3', 'stage4'];
      return stageNames.indexOf(this.stage.name);
    },

    onAnswersReorder(questionIndex) {
      // Emette un evento per notificare il riordinamento delle risposte
      const stageIndex = this.getStageIndex();
      this.$emit('answers-reordered', stageIndex, questionIndex);
    },

    onRestart() {
      // Reset completo per la preview
      this.status.currentStep = 1;
      this.status.currentStage = 1;
      this.selectedAnswer = null;
      this.selectedCheckboxes = [];

      // Reset di tutti gli stage
      const allStages = ['stage1', 'stage2', 'stage3', 'stage4'];
      allStages.forEach((stageName) => {
        if (this.status.stages[stageName]) {
          this.$set(this.status.stages[stageName], 'questions', {});
          if (stageName === 'stage4') {
            this.$set(this.status.stages[stageName], 'phases', {});
          }
          this.status.stages[stageName].complete = false;
        }
      });

      this.status.complete = false;

      this.$emit('input', this.status);
    },

    onContinue() {
      // In modalità preview, verifica se ci sono ancora domande incomplete nello stage
      if (this.mode === 'preview') {
        const incompleteQuestions = this.questions.filter(
          (question) => !this.isQuestionComplete(question),
        );

        if (incompleteQuestions.length > 0) {
          // Avanza alla prossima domanda incompleta
          const nextIncompleteIndex = this.questions.findIndex(
            (question) => !this.isQuestionComplete(question),
          );
          if (nextIncompleteIndex !== -1) {
            this.status.currentStep = nextIncompleteIndex + 1;
            this.$emit('input', this.status);
            return;
          }
        }
      }

      // Se tutte le domande sono complete, avanza al prossimo stage
      this.status.currentStage += 1;
      this.status.currentStep = 1;
      this.$emit('input', this.status);
    },

    addQuestion() {
      // Emette un evento per aggiungere una nuova domanda
      const stageIndex = this.getStageIndex();
      this.$emit('add-question', stageIndex);
    },

    removeQuestion(questionIndex) {
      const stageIndex = this.getStageIndex();
      this.$emit('remove-question', stageIndex, questionIndex);
    },

    addAnswer(questionIndex) {
      const stageIndex = this.getStageIndex();
      this.$emit('add-answer', stageIndex, questionIndex);
    },

    removeAnswer(questionIndex, answerIndex) {
      const stageIndex = this.getStageIndex();
      this.$emit('remove-answer', stageIndex, questionIndex, answerIndex);
    },

    confirmCheckboxSelections(questionIndex) {
      const question = this.questions[questionIndex];
      if (!question || !question.id) return;

      // Salva le risposte selezionate nello stato
      if (!this.status.stages[this.stage.name].questions) {
        this.$set(this.status.stages[this.stage.name], 'questions', {});
      }

      // Per le checkbox, salviamo un array di risposte
      this.$set(this.status.stages[this.stage.name].questions, question.id, {
        complete: true,
        answers: this.selectedCheckboxes.map((answer) => ({
          title: answer.title,
          id: answer.id,
        })),
        answerIds: this.selectedCheckboxes.map((answer) => answer.id),
      });

      // Svuota le selezioni
      this.selectedCheckboxes = [];

      // Avanza alla prossima domanda
      this.$nextTick(() => {
        const nextQuestionIndex = this.questions.findIndex(
          (q, idx) => idx > questionIndex
            && this.isQuestionVisible(q) && !this.isQuestionComplete(q),
        );
        if (nextQuestionIndex !== -1) {
          this.status.currentStep = nextQuestionIndex + 1;
        } else if (this.isStageComplete) {
          // Se non ci sono altre domande visibili, controlla se lo stage è completo
          this.status.currentStep = this.questions.length + 1; // Collassa l'ultima domanda
        }
      });

      // Emetti l'aggiornamento dello stato
      this.$emit('input', this.status);
    },

    getQuestionTitle(question) {
      // In modalità edit, mostra solo il titolo
      if (this.mode === 'edit') {
        return question.title;
      }

      // In modalità preview, mostra il titolo con la risposta se completata
      if (!this.status || !this.status.stages[this.stage.name] || !question.id) {
        return question.title;
      }

      const stageQuestions = this.status.stages[this.stage.name].questions;
      const questionResponse = stageQuestions[question.id];

      if (questionResponse && questionResponse.complete) {
        // Per le checkbox, mostra tutte le risposte selezionate
        if (questionResponse.answers && questionResponse.answers.length > 0) {
          const answersText = questionResponse.answers.map((answer) => answer.title).join(', ');
          return `${question.title}&nbsp;<strong>${answersText}</strong>`;
        }
        // Per le altre tipologie, mostra la singola risposta
        if (questionResponse.answer) {
          return `${question.title}&nbsp;<strong>${questionResponse.answer}</strong>`;
        }
      }

      return question.title;
    },
  },
};
</script>

<style scoped>
.component-testeditorstage::v-deep .v-stepper__label {
  text-align: left !important;
  width: 100% !important;
}

.component-testeditorstage::v-deep .v-stepper--vertical .v-stepper__step {
  padding: 16px 24px;
}

.component-testeditorstage::v-deep .v-stepper__step.question-selectable {
  cursor: pointer;
  transition: background-color 0.2s;
}

.component-testeditorstage::v-deep .v-stepper__step.question-selectable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Evidenziazione della domanda selezionata (::v-deep per applicare al DOM Vuetify) */
.component-testeditorstage::v-deep .v-stepper__step.question-selected {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-radius: 4px !important;
  z-index: 2 !important;
  position: relative !important;
}

.component-testeditorstage::v-deep .v-stepper__step.question-selected .v-stepper__step__step {
  background-color: #1976d2 !important;
  color: white !important;
}

.component-testeditorstage::v-deep .v-stepper__step.question-selected .v-stepper__label {
  color: #1976d2 !important;
  font-weight: 600 !important;
}

.answer-selectable {
  position: relative;
  cursor: pointer;
}

.answer-selectable:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.answer-selectable::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

/* Evidenziazione della risposta selezionata */
.answer-selected {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-radius: 4px !important;
  color: #1976d2 !important;
  font-weight: 600 !important;
}

.answer-selected .v-label {
  color: #1976d2 !important;
  font-weight: 600 !important;
}

.stage-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 16px;
}

.question-answers {
  min-height: 100px;
}

.bigbutton {
  min-height: 200px;
  max-width: 100%;
  white-space: normal;
  letter-spacing: initial;
}

.bigbutton >>> .v-btn__content {
  max-width: 100%;
}

.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.question-title {
  flex: 1;
  margin-right: 16px;
}

.delete-btn {
  flex-shrink: 0;
}

.answer-wrapper {
  position: relative;
  width: 100%;
}

.answer-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  z-index: 2;
  flex-direction: row-reverse;
}

.answer-actions-drag {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
}

.answer-actions-delete {
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 2;
}

.answer-conditions {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
}

.answer-actions-horizontal {
  position: relative;
  z-index: 2;
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: 8px;
  flex-direction: row-reverse;
}

.condition-warning-btn {
  opacity: 0.8;
}

.condition-warning-btn:hover {
  opacity: 1;
}

.answer-item {
  margin-bottom: 8px;
}

/* Stili per domande nascoste */
.question-hidden {
  font-style: italic;
  color: #666 !important;
}

/* Stili per risposte nascoste */
.answer-hidden {
  opacity: 0.6;
}

.answer-hidden.bigbutton {
  border: 2px dashed #ccc !important;
  background-color: #f5f5f5 !important;
  color: #666 !important;
}

.answer-hidden .v-label {
  color: #999 !important;
}

.answer-hidden .v-input__slot {
  background-color: #f5f5f5 !important;
}

/* Stili per i pulsanti Nuova Risposta */
.add-answer-btn {
  border: 2px dashed #4caf50 !important;
  background-color: #f8f9fa !important;
  color: #4caf50 !important;
  transition: all 0.2s ease;
}

.add-answer-btn:hover {
  border-color: #2e7d32 !important;
  background-color: #e8f5e8 !important;
  color: #2e7d32 !important;
}

.add-answer-item {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.add-answer-item:hover {
  opacity: 1;
}

.add-answer-radio,
.add-answer-checkbox {
  opacity: 0.7;
}

.add-answer-radio .v-label,
.add-answer-checkbox .v-label {
  color: #666 !important;
  font-style: italic;
}

/* Stili per domande nascoste */
.question-hidden {
  color: #999 !important;
}

/* Stili per il wrapper del titolo della domanda */
.question-title-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
}

/* Stili per il pulsante Nuova Domanda */
.add-question-step {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: 2px dashed #4caf50 !important;
  border-radius: 4px !important;
  margin: 8px 0 !important;
  background-color: #f8f9fa !important;
}

.add-question-step:hover {
  background-color: #e8f5e8 !important;
  border-color: #2e7d32 !important;
}

.add-question-step .v-stepper__step__step {
  background-color: #4caf50 !important;
  color: white !important;
}

.add-question-title {
  color: #4caf50 !important;
  font-weight: 500;
}

/* Stili per il drag and drop */
.sortableAnswer {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.sortableAnswer:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.sortHandle {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.sortHandle:hover {
  opacity: 1;
}

/* Stili per il draggable container */
.draggable-container {
  min-height: 50px;
}

/* Stili per il draggable container dei buttons */
.draggable-buttons-container {
  display: flex;
  flex-wrap: wrap;
  margin-left: -12px;
  margin-right: -12px;
}

/* Stili per il pulsante Conferma scelte delle checkbox */
.confirm-checkboxes-item {
  margin-top: 16px;
  text-align: center;
}

.confirm-checkboxes-btn {
  min-width: 150px;
  font-weight: 500;
}

.draggable-buttons-container > .v-col {
  padding-left: 12px;
  padding-right: 12px;
}

/* Stili per l'elemento in fase di drag */
.sortableAnswer.sortable-ghost {
  opacity: 0.5;
  transform: rotate(5deg);
}

.sortableAnswer.sortable-chosen {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  transform: scale(1.02);
}
</style>
